import React, {useEffect, useState} from "react";
import {Col, Row} from "reactstrap";
import Dialog from "../../base/dialog";
import Divider from "@mui/material/Divider";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Form from "../../base/form";
import useIsMounted from "../../../hooks/use-is-mounted";
import {formatMoney} from "../../../../core/services/utils/utils";
import Api from "../../../../core/services/api/api";
import {OutlinedDropdownForm} from "../../base/dropdown/outlined_dropdown";
import ToastService from "../../../../core/services/toast";


const schema = Yup.object().shape({
    amount: Yup.number().nullable().required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('Number')),
    reason: Yup.object().nullable().required(ValidateMessages.required),
})


const RefillGiftCardModal = ({open, setOpen, onSuccess, giftCard}) => {
    const [submitting, setSubmitting] = useState(false);
    const [reasons, setReasons] = useState([]);
    const validate = makeValidate(schema);
    const required = makeRequired(schema);
    const isMounted = useIsMounted();

    /**
     * Warns the user when the main API is used in the development environment.
     */
    useEffect(() => {
        if (open) {
            ToastService.mainAPIRedZoneWarning();
        }
    }, [open])

    /**
     * Listens for the changes in open state and if open, then gets the reasons for gift card refills
     */
    useEffect(() => {
        if (!open) return;
        getReasonsForRefill();
    }, [open])

    /**
     * Fetches the list of reasons for refilling the gift card
     */
    const getReasonsForRefill = () => {
        Api.getRefillReasonsForGiftCards().then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setReasons(response.data);
            }
        })
    }


    /**
     * Refills the amount of a gift card by calling the api server and if the result was successful, calls the
     * onSuccess callback
     * with the newly created gift card as its argument.
     * @param values {any} from values
     */
    const refillGiftCard = (values) => {
        setSubmitting(true);
        const forApi = {
            amount: parseFloat(values.amount),
            cardNumber: giftCard?.cardNumber,
            reasonId: values.reason?.id,
        }
        Api.refillGiftCard(forApi).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                onSuccess({...giftCard, currentBalance: parseFloat(values.amount) + giftCard?.currentBalance});
                setOpen(false);
            }
            setSubmitting(false);
        })
    }

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'sm'}
            className={'refill-gift-card-dialog'}
        >
            <Row className='no-gutters justify-content-center align-items-start w-100'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className={'title'}>
                        Gift Card Refill
                    </p>
                </Row>

                <Divider className='divider'/>
                <div className='my-3 w-100'/>
                <Form
                    className='w-100'
                    onSubmit={refillGiftCard}
                    validate={validate}
                    initialValues={{amount: '0'}}
                    render={({values, form}) => {
                        return (
                            <Row className='no-gutters justify-content-around w-100'>
                                <Row className='no-gutters justify-content-around w-100 mx-4'>
                                    <Col xs={12} className={'my-3 mb-4 w-100'}>
                                        <p className={'description'}>
                                            Please enter the amount that you would like to add to the selected gift card
                                        </p>
                                    </Col>
                                    <Col xs={12} className='px-2 mb-4 d-flex align-items-center justify-content-center'>
                                        <p className={'current-balance'}>
                                            {formatMoney(giftCard?.currentBalance)}
                                            <span className={'plus-sign'}>+</span>
                                        </p>
                                        <OutlinedInputForm
                                            label={'Amount'}
                                            type={'number'}
                                            name={`amount`}
                                            className={'mx-2'}
                                            required={required.amount}
                                            value={values.amount ?? '0'}
                                            onChange={(e) => form.change(e.target.name, (Math.max(parseFloat(e.target.value), 0)).toString())}
                                        />
                                        <p className={'future-balance'}>
                                            <span className={'equal-sign'}>=</span>
                                            {formatMoney((giftCard?.currentBalance ?? 0) + parseFloat(values.amount))}
                                        </p>
                                    </Col>
                                    <Col xs={12} className='px-8 mb-4'>
                                        <OutlinedDropdownForm
                                            fontSize={14}
                                            fullWidth={true}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                            label={'Refill Reason'}
                                            name={'reason'}
                                            required={!!required.reason}
                                            data={reasons?.map(e => ({
                                                value: e,
                                                label: e.title,
                                                id: e.id,
                                            })) ?? []}
                                        />
                                    </Col>
                                </Row>
                                <div className='my-2 w-100'/>
                                <Divider className='divider'/>
                                <Row className='no-gutters justify-content-between align-items-start w-100'>
                                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                                        <Button
                                            disabled={!open || submitting}
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => setOpen(false)}
                                            borderRadius={'0 8px 0 8px'}
                                        >
                                            {submitting ? 'submitting...' : 'Close'}
                                        </Button>
                                        <Button
                                            disabled={!open || submitting || (values.amount ?? '0') === '0'}
                                            variant={'dialog'}
                                            color={'primary'}
                                            type={'submit'}
                                            borderRadius={'8px 0 8px 0'}
                                        >
                                            {submitting ? 'submitting...' : 'Refill'}
                                        </Button>
                                    </Row>
                                </Row>
                            </Row>
                        );
                    }}
                />
            </Row>
        </Dialog>
    );
}

export default RefillGiftCardModal;
